/**
 * Feel-first input: sampled fresh from the host snapshot every render frame,
 * mapped into a CAMERA-relative move vector. No receipts, no quantization —
 * W is always up-screen, D is always right-screen, period.
 *
 * Press events (jump/dash/reset) are latched here per frame and consumed by
 * the fixed-step controller via short buffer timers, so a tap between physics
 * steps is never dropped.
 */
import type { GameContext } from "@host/types.js";

export interface InputFrame {
  /** camera-relative wish direction on the ground plane, |v| <= 1 */
  moveX: number;
  moveZ: number;
  jumpHeld: boolean;
  jumpPressed: boolean;
  dashPressed: boolean;
  resetPressed: boolean;
  /** grab beam: left mouse or E */
  grabHeld: boolean;
  grabPressed: boolean;
  grabReleased: boolean;
  /** portal gun: Q = blue entry (A), R = orange exit (B) */
  portalAPressed: boolean;
  portalBPressed: boolean;
  purgePressed: boolean;
  /** raw cursor position in canvas CSS pixels (for the aim ray) */
  mouseX: number;
  mouseY: number;
  /** gamepad right stick (screen-relative, deadzone applied); 0,0 = centered */
  aimStickX: number;
  aimStickY: number;
  gamepadConnected: boolean;
  /** the pad is actively providing input this frame (device-aware prompts) */
  padActive: boolean;
  /** keyboard is actively providing input this frame */
  kbActive: boolean;
  /** tutorial skip: Esc or pad Start */
  skipPressed: boolean;
}

const clampUnit = (v: number): number => (v < -1 ? -1 : v > 1 ? 1 : v);

export class InputSampler {
  constructor(
    private readonly ctx: GameContext,
    /**
     * Live camera yaw source in radians (rotation about +Y); the movement
     * basis derives from it every sample. The Engine wires this to
     * CameraRig.inputYaw so W stays up-screen in the chamber view and DURING
     * blends — the rig reports the blend destination's yaw by design
     * (LOCKED-DESIGN 2.2 screen-relative controls).
     */
    private readonly cameraYaw: () => number,
  ) {}

  sample(): InputFrame {
    const snap = this.ctx.getInput();
    const keys = snap.keyboard.keys;
    const just = snap.keyboard.justPressed;

    // Raw screen-space intent: x = right-screen, z = up-screen.
    let sx = 0;
    let sz = 0;
    if (keys.has("KeyD") || keys.has("ArrowRight")) sx += 1;
    if (keys.has("KeyA") || keys.has("ArrowLeft")) sx -= 1;
    if (keys.has("KeyW") || keys.has("ArrowUp")) sz += 1;
    if (keys.has("KeyS") || keys.has("ArrowDown")) sz -= 1;

    // Gamepad left stick overrides keys when deflected.
    const gp = snap.gamepad;
    if (gp.connected && gp.axes.length >= 2) {
      const ax = gp.axes[0] ?? 0;
      const ay = gp.axes[1] ?? 0;
      if (Math.hypot(ax, ay) > 0.18) {
        sx = clampUnit(ax);
        sz = clampUnit(-ay); // stick up = up-screen
      }
    }

    // Normalize diagonals so WASD diagonal isn't 41% faster.
    const len = Math.hypot(sx, sz);
    if (len > 1) {
      sx /= len;
      sz /= len;
    }

    // Rotate screen intent into world space by the camera's yaw.
    // "Up-screen" is the camera's forward projected onto the ground plane.
    const yaw = this.cameraYaw();
    const cos = Math.cos(yaw);
    const sin = Math.sin(yaw);
    const moveX = sx * cos - sz * sin;
    const moveZ = -sz * cos - sx * sin;

    // standard-mapping pad, kid-friendly layout:
    //   A(0)=jump · B(1) or LT(6)=dash · X(2) or RT(7)=grab (hold→vacuum,
    //   release→volley) · LB(4)=blue portal · RB(5)=orange · Select(8)=reset
    const gpc = gp.connected;
    const grabHeld =
      (snap.mouse.buttons & 1) !== 0 ||
      keys.has("KeyE") ||
      (gpc && ((gp.buttons[2] ?? false) || (gp.buttons[7] ?? false)));
    const grabPressed = grabHeld && !this.prevGrabHeld;
    const grabReleased = !grabHeld && this.prevGrabHeld;
    this.prevGrabHeld = grabHeld;

    // right stick aim (screen-relative), circular deadzone
    let aimStickX = 0;
    let aimStickY = 0;
    if (gpc && gp.axes.length >= 4) {
      const ax = gp.axes[2] ?? 0;
      const ay = gp.axes[3] ?? 0;
      if (Math.hypot(ax, ay) > 0.25) {
        aimStickX = clampUnit(ax);
        aimStickY = clampUnit(ay);
      }
    }

    return {
      moveX,
      moveZ,
      jumpHeld: keys.has("Space") || (gpc && (gp.buttons[0] ?? false)),
      jumpPressed: just.has("Space") || (gpc && (gp.justPressed[0] ?? false)),
      dashPressed:
        just.has("ShiftLeft") ||
        just.has("ShiftRight") ||
        (gpc && ((gp.justPressed[1] ?? false) || (gp.justPressed[6] ?? false))),
      resetPressed: just.has("KeyT") || (gpc && (gp.justPressed[8] ?? false)),
      grabHeld,
      grabPressed,
      grabReleased,
      portalAPressed: just.has("KeyQ") || (gpc && (gp.justPressed[4] ?? false)),
      portalBPressed: just.has("KeyR") || (gpc && (gp.justPressed[5] ?? false)),
      purgePressed: just.has("KeyC") || (gpc && (gp.justPressed[13] ?? false)),
      mouseX: snap.mouse.x,
      mouseY: snap.mouse.y,
      aimStickX,
      aimStickY,
      gamepadConnected: gpc,
      padActive:
        gpc &&
        (Math.hypot(gp.axes[0] ?? 0, gp.axes[1] ?? 0) > 0.25 ||
          aimStickX !== 0 ||
          aimStickY !== 0 ||
          gp.buttons.some(Boolean)),
      kbActive: keys.size > 0,
      skipPressed: just.has("Escape") || (gpc && (gp.justPressed[9] ?? false)),
    };
  }

  private prevGrabHeld = false;
}
